import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

function changelogJsonUrl(baseUrl = import.meta.env.BASE_URL || '/') {
  const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  return `${base}changelog.json`;
}

function useChangelog(baseUrl) {
  const [state, setState] = useState({ loading: true, error: '', versions: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(changelogJsonUrl(baseUrl));
        if (!response.ok) throw new Error('Changelog is not available.');
        const data = await response.json();
        if (!cancelled) {
          setState({
            loading: false,
            error: '',
            versions: Array.isArray(data.versions) ? data.versions : [],
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            loading: false,
            error: error.message || 'Changelog is not available.',
            versions: [],
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  return state;
}

export function ChangelogView({ baseUrl, currentVersion }) {
  const { loading, error, versions } = useChangelog(baseUrl);

  if (loading) {
    return <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Loading changelog…</p>;
  }
  if (error) {
    return <p style={{ margin: 0, color: 'var(--text-secondary)' }}>{error}</p>;
  }
  if (versions.length === 0) {
    return <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No releases have been published yet.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {versions.map((entry) => {
        const isCurrent = currentVersion && entry.version === currentVersion;
        return (
          <section key={`${entry.version}-${entry.date}`}>
            <h2 style={{ margin: '0 0 0.35rem', fontSize: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}>
              <span>{entry.version}</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 500 }}>{entry.date}</span>
              {isCurrent && (
                <span style={{ color: 'var(--accent, #2563eb)', fontSize: '0.75rem', fontWeight: 600 }}>This build</span>
              )}
            </h2>
            {(entry.sections || []).map((section) => (
              <div key={section.title} style={{ marginTop: '0.55rem' }}>
                <h3 style={{ margin: '0 0 0.25rem', fontSize: '0.75rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                  {section.title}
                </h3>
                <ul style={{ margin: 0, paddingLeft: '1.15rem' }}>
                  {(section.items || []).map((item) => (
                    <li key={item} style={{ margin: '0.15rem 0' }}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}

function ChangelogDialog({ version, baseUrl, onClose }) {
  const titleId = useId();

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5000,
        padding: '1.5rem',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          background: 'var(--bg-card, var(--bg-primary, #111))',
          color: 'var(--text-primary, inherit)',
          border: '1px solid var(--border, rgba(255, 255, 255, 0.12))',
          borderRadius: '12px',
          width: 'min(640px, 100%)',
          maxHeight: '80vh',
          overflow: 'auto',
          padding: '1.25rem 1.4rem 1.1rem',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <h2 id={titleId} style={{ margin: 0, fontSize: '1.15rem', color: 'var(--text-primary, inherit)' }}>Changelog</h2>
          <button type="button" onClick={onClose} style={{
            background: 'transparent',
            color: 'var(--text-secondary, inherit)',
            border: '1px solid var(--border, rgba(255, 255, 255, 0.12))',
            borderRadius: '6px',
            padding: '0.35rem 0.7rem',
            cursor: 'pointer',
          }}>
            Close
          </button>
        </div>
        <ChangelogView baseUrl={baseUrl} currentVersion={version} />
        <p style={{ margin: '1rem 0 0', fontSize: '0.85rem' }}>
          <a href="/changelog">Open the changelog page</a>
        </p>
      </div>
    </div>,
    document.body,
  );
}

export function VersionButton({ collapsed = false, align, baseUrl }) {
  const [open, setOpen] = useState(false);
  const { versions } = useChangelog(baseUrl);
  const version = versions[0]?.version || '';
  const textAlign = align || (collapsed ? 'center' : 'left');
  const label = version
    ? (collapsed ? `v${version}` : `Version: ${version}`)
    : (collapsed ? 'v' : 'Version');

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="View changelog"
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          color: 'inherit',
          font: 'inherit',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          textAlign,
          width: '100%',
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
      {open && (
        <ChangelogDialog
          version={version}
          baseUrl={baseUrl}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function ChangelogPage({ standalone = false }) {
  const { versions } = useChangelog();
  const version = versions[0]?.version || '';
  return (
    <div style={{
      width: standalone ? '100vw' : '100%',
      alignSelf: 'stretch',
      flex: standalone ? '1 0 auto' : 1,
      minHeight: standalone ? '100vh' : 0,
      overflow: 'auto',
      boxSizing: 'border-box',
      background: 'var(--bg-page, var(--bg-primary))',
      color: 'var(--text-primary)',
      padding: '1.5rem 2rem 2.5rem',
    }}>
      <h1 style={{ margin: '0 0 1rem', fontSize: '1.4rem' }}>Changelog</h1>
      <ChangelogView currentVersion={version} />
    </div>
  );
}
